import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { LoginPage, ForgotPage, ResetPage } from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CompetenceMatrix from "@/pages/CompetenceMatrix";
import Stations from "@/pages/Stations";
import Schedule from "@/pages/Schedule";
import RotationWarnings from "@/pages/RotationWarnings";
import Employees from "@/pages/Employees";
import Absence from "@/pages/Absence";
import Integrations from "@/pages/Integrations";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Organisation from "@/pages/Organisation";
import MyDepartment from "@/pages/MyDepartment";
import Production from "@/pages/Production";
import ShiftPatterns from "@/pages/ShiftPatterns";
import Enkater from "@/pages/Enkater";
import Utskick from "@/pages/Utskick";
import Feed from "@/pages/Feed";
import TidigareHemgang from "@/pages/TidigareHemgang";
import Support from "@/pages/Support";
import ContentEditor from "@/pages/ContentEditor";
import Internrekrytering from "@/pages/Internrekrytering";
import Chat from "@/pages/Chat";
import Arbetsmiljo from "@/pages/Arbetsmiljo";
import Utbildningar from "@/pages/Utbildningar";
import Uppdrag from "@/pages/Uppdrag";
import Bibliotek from "@/pages/Bibliotek";
import Statistik from "@/pages/Statistik";
import Skraddare from "@/pages/Skraddare";
import Rollskraddare from "@/pages/Rollskraddare";
import Struktur from "@/pages/Struktur";
import Moten from "@/pages/Moten";
import CVPage from "@/pages/CV";
import ITSupport from "@/pages/ITSupport";
import Placeringar from "@/pages/Placeringar";
import Tillganglighet from "@/pages/Tillganglighet";
import { useEffect } from "react";
import { applyBrandColors } from "@/lib/theme";
import {
  EmployeeHome, EmployeeSchedule, EmployeeQuestions, EmployeeLeave, EmployeeProfile,
} from "@/pages/Employee";

function Splash() {
  return (
    <div className="grid min-h-screen place-items-center bg-primary text-primary-foreground">
      <div className="flex flex-col items-center gap-3">
        <img src="/icon-192.png" alt="" className="h-14 w-14 animate-pulse rounded-2xl" />
        <p className="text-sm text-blue-200">Laddar…</p>
      </div>
    </div>
  );
}

function ThemeApplier() {
  const { user } = useAuth();
  useEffect(() => {
    if (user) applyBrandColors(user.color_primary, user.color_accent);
  }, [user]);
  return null;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (["owner", "master_admin"].includes(user?.role) && (!user?.company_id || !user?.company_wizard_done))
    return <Navigate to="/skraddare" replace />;
  return <Navigate to="/flode" replace />;
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace state={{ from: loc }} />;
  return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <ThemeApplier />
      <Routes>
        <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPage /></PublicOnly>} />
        <Route path="/reset-password" element={<ResetPage />} />

        <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
        <Route path="/onboarding" element={<Protected><Skraddare /></Protected>} />
        <Route path="/skraddare" element={<Protected><Skraddare /></Protected>} />
        <Route path="/rollskraddare" element={<Protected><Rollskraddare /></Protected>} />
        <Route path="/struktur" element={<Protected><Struktur /></Protected>} />
        <Route path="/moten" element={<Protected><Moten /></Protected>} />
        <Route path="/cv" element={<Protected><CVPage /></Protected>} />
        <Route path="/it-support" element={<Protected><ITSupport /></Protected>} />
        <Route path="/placeringar" element={<Protected><Placeringar /></Protected>} />
        <Route path="/tillganglighet" element={<Protected><Tillganglighet /></Protected>} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/min-avdelning" element={<Protected><MyDepartment /></Protected>} />
        <Route path="/medarbetare" element={<Protected><Employees /></Protected>} />
        <Route path="/organisation" element={<Protected><Organisation /></Protected>} />
        <Route path="/kompetensmatris" element={<Protected><CompetenceMatrix /></Protected>} />
        <Route path="/stationer" element={<Protected><Stations /></Protected>} />
        <Route path="/bemanningsbehov" element={<Protected><Production /></Protected>} />
        <Route path="/skiftmonster" element={<Protected><ShiftPatterns /></Protected>} />
        <Route path="/enkater" element={<Protected><Enkater /></Protected>} />
        <Route path="/utskick" element={<Protected><Utskick /></Protected>} />
        <Route path="/flode" element={<Protected><Feed /></Protected>} />
        <Route path="/tidigare-hemgang" element={<Protected><TidigareHemgang /></Protected>} />
        <Route path="/support" element={<Protected><Support /></Protected>} />
        <Route path="/internrekrytering" element={<Protected><Internrekrytering /></Protected>} />
        <Route path="/meddelanden" element={<Protected><Chat /></Protected>} />
        <Route path="/innehall" element={<Protected><ContentEditor /></Protected>} />
        <Route path="/schema" element={<Protected><Schedule /></Protected>} />
        <Route path="/rotation" element={<Protected><RotationWarnings /></Protected>} />
        <Route path="/arbetsmiljo" element={<Protected><Arbetsmiljo /></Protected>} />
        <Route path="/utbildningar" element={<Protected><Utbildningar /></Protected>} />
        <Route path="/uppdrag" element={<Protected><Uppdrag /></Protected>} />
        <Route path="/bibliotek" element={<Protected><Bibliotek /></Protected>} />
        <Route path="/statistik" element={<Protected><Statistik /></Protected>} />
        <Route path="/franvaro" element={<Protected><Absence /></Protected>} />
        <Route path="/integrationer" element={<Protected><Integrations /></Protected>} />
        <Route path="/audit" element={<Protected><Audit /></Protected>} />
        <Route path="/installningar" element={<Protected><Settings /></Protected>} />

        <Route path="/idag" element={<Protected><EmployeeHome /></Protected>} />
        <Route path="/emp-schema" element={<Protected><EmployeeSchedule /></Protected>} />
        <Route path="/fragor" element={<Protected><EmployeeQuestions /></Protected>} />
        <Route path="/ledighet" element={<Protected><EmployeeLeave /></Protected>} />
        <Route path="/profil" element={<Protected><EmployeeProfile /></Protected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
